import { useState, useEffect, memo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Project {
  id: string;
  title: string;
  description: string;
  images: string[];
  tags: string[];
  video_url?: string | null;
  video_urls?: string[];
  client_name?: string | null;
}

const getYouTubeEmbedUrl = (url: string): string => {
  let videoId = '';
  
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (watchMatch) {
    videoId = watchMatch[1];
  }
  
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) {
    videoId = shortsMatch[1];
  }
  
  const embedMatch = url.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) {
    videoId = embedMatch[1];
  }
  
  if (videoId) {
    return `https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0`;
  }
  
  return url;
};

const getYouTubeThumbnail = (url: string): string => {
  let videoId = '';
  
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (watchMatch) videoId = watchMatch[1];
  
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) videoId = shortsMatch[1];
  
  const embedMatch = url.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) videoId = embedMatch[1];
  
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  
  return '';
};

// Lazy YouTube embed component - shows thumbnail until clicked
const LazyYouTubeEmbed = memo(({ url, title }: { url: string; title: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const thumbnail = getYouTubeThumbnail(url);

  if (isLoaded) {
    return (
      <iframe
        src={getYouTubeEmbedUrl(url)}
        title={title}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <div 
      className="w-full h-full relative cursor-pointer group/video"
      onClick={() => setIsLoaded(true)}
    >
      <img
        src={thumbnail}
        alt={title}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover/video:bg-black/40 transition-colors">
        <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center group-hover/video:scale-110 transition-transform">
          <Play className="w-8 h-8 text-primary-foreground ml-1" fill="currentColor" />
        </div>
      </div>
    </div>
  );
});

LazyYouTubeEmbed.displayName = 'LazyYouTubeEmbed';

// Memoized project card component
const ProjectCard = memo(({ 
  project, 
  index, 
  onOpenLightbox,
  t 
}: { 
  project: Project; 
  index: number;
  onOpenLightbox: (project: Project, imageIndex: number) => void;
  t: (key: string) => string;
}) => {
  const youtubeVideos = project.video_urls?.length ? project.video_urls : (project.video_url ? [project.video_url] : []);
  const hasMultipleVideos = youtubeVideos.length > 1;
  const hasMultipleImages = project.images && project.images.length > 1;

  const renderVideoContent = () => {
    // Single video - no carousel overhead
    if (youtubeVideos.length === 1) {
      return (
        <div className="aspect-video relative overflow-hidden">
          <LazyYouTubeEmbed url={youtubeVideos[0]} title={project.title} />
        </div>
      );
    }

    // Multiple videos - use carousel
    return (
      <Carousel className="w-full">
        <CarouselContent>
          {youtubeVideos.map((videoUrl, idx) => (
            <CarouselItem key={idx}>
              <div className="aspect-video relative overflow-hidden">
                <LazyYouTubeEmbed url={videoUrl} title={`${project.title} - Video ${idx + 1}`} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2" />
        <CarouselNext className="right-2" />
      </Carousel>
    );
  };

  const renderImageContent = () => {
    // Single image - no carousel overhead
    if (project.images.length === 1) {
      return (
        <div 
          className="aspect-video bg-gradient-to-br from-holographic-cyan/20 to-holographic-pink/20 relative overflow-hidden cursor-pointer"
          onClick={() => onOpenLightbox(project, 0)}
        >
          <img
            src={project.images[0]}
            alt={project.title}
            loading="lazy"
            className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      );
    }

    // Multiple images - use carousel
    return (
      <Carousel className="w-full">
        <CarouselContent>
          {project.images.map((imageUrl, idx) => (
            <CarouselItem key={idx}>
              <div 
                className="aspect-video bg-gradient-to-br from-holographic-cyan/20 to-holographic-pink/20 relative overflow-hidden cursor-pointer"
                onClick={() => onOpenLightbox(project, idx)}
              >
                <img
                  src={imageUrl}
                  alt={`${project.title} - Image ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2" />
        <CarouselNext className="right-2" />
      </Carousel>
    );
  };

  return (
    <Card
      className="project-card group overflow-hidden glass-card hover:shadow-xl transition-all duration-300 hover:-translate-y-2 animate-fade-in"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {youtubeVideos.length > 0 ? (
        <div className="relative">
          {renderVideoContent()}
          {hasMultipleVideos && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded z-10">
              {youtubeVideos.length} {t("portfolio.videos")}
            </div>
          )}
        </div>
      ) : project.images && project.images.length > 0 ? (
        <div className="relative">
          {renderImageContent()}
          {hasMultipleImages && (
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded z-10">
              {project.images.length} {t("portfolio.photos")}
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-gradient-to-br from-holographic-cyan/20 to-holographic-pink/20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      )}
      <CardContent className="p-6">
        <h3 className="text-xl font-bold mb-2 text-glow-hover group-hover:text-holographic transition-colors">
          {project.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {project.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-xs"
            >
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});

ProjectCard.displayName = 'ProjectCard';

const PortfolioSection = () => {
  const { t } = useLanguage();
  const [activeClient, setActiveClient] = useState<string>("all");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data, error } = await supabase
          .from("portfolio_projects")
          .select("*")
          .order("order_index", { ascending: true });

        if (error) throw error;
        setProjects((data || []) as Project[]);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const clientNames = [...new Set(projects.map(p => p.client_name).filter((name): name is string => !!name))];

  const filteredProjects = activeClient === "all" 
    ? projects 
    : projects.filter(p => p.client_name === activeClient);

  const openLightbox = (project: Project, imageIndex: number) => {
    setCurrentProject(project);
    setCurrentImageIndex(imageIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (currentProject && currentImageIndex < currentProject.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, currentImageIndex, currentProject]);

  return (
    <section id="portfolio" className="py-16 md:py-24 px-4 md:px-6 relative overflow-hidden min-h-screen flex items-center">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-center text-glow-hover uppercase">
          {t("portfolio.title")}
        </h2>
        <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          {t("portfolio.subtitle")}
        </p>

        {/* Client Filters */}
        {clientNames.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setActiveClient("all")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeClient === "all"
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-105"
                  : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {t("portfolio.allClients")}
            </button>
            {clientNames.map((clientName) => (
              <button
                key={clientName}
                onClick={() => setActiveClient(clientName)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeClient === clientName
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-105"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {clientName}
              </button>
            ))}
          </div>
        )}

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">{t("portfolio.loading")}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">{t("portfolio.noProjects")}</p>
            </div>
          ) : (
            filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                onOpenLightbox={openLightbox}
                t={t}
              />
            ))
          )}
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black/95 border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
              onClick={closeLightbox}
            >
              <X className="h-6 w-6" />
            </Button>

            {currentProject && currentProject.images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 disabled:opacity-30 w-16 h-16 bg-black/50 backdrop-blur-sm shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all"
                  onClick={prevImage}
                  disabled={currentImageIndex === 0}
                >
                  <ChevronLeft className="h-12 w-12" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 disabled:opacity-30 w-16 h-16 bg-black/50 backdrop-blur-sm shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all"
                  onClick={nextImage}
                  disabled={currentImageIndex === currentProject.images.length - 1}
                >
                  <ChevronRight className="h-12 w-12" />
                </Button>
              </>
            )}

            {currentProject && (
              <div className="relative w-full h-full overflow-hidden">
                <div 
                  className="flex h-full transition-transform duration-500 ease-out will-change-transform"
                  style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                >
                  {currentProject.images.map((imageUrl, idx) => (
                    <div 
                      key={idx}
                      className="min-w-full h-full flex items-center justify-center p-12"
                    >
                      <img
                        src={imageUrl}
                        alt={`${currentProject.title} - Image ${idx + 1}`}
                        className="max-w-full max-h-[80vh] object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                
                {currentProject.images.length > 1 && (
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/80 text-white px-4 py-2 rounded-full text-sm z-10">
                    {currentImageIndex + 1} / {currentProject.images.length}
                  </div>
                )}

                <div className="absolute bottom-4 left-4 bg-black/80 text-white p-4 rounded-lg max-w-md z-10">
                  <h3 className="font-bold text-lg mb-1">{currentProject.title}</h3>
                  <p className="text-sm text-white/80">{currentProject.description}</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PortfolioSection;
